'use client';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { IAtdropdownProps, IOption } from './at-dropdown.types';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';

/**
 * `AtDropdown` is a fully styled, accessible `<select>` component for use in filters and UI controls.
 * It supports optional icons, dynamic option lists, keyboard-only focus ring styling,
 * and appearance customization through background color and border props.
 *
 * This component is not form-library aware (e.g., not tied to React Hook Form) and is typically
 * used in places like `OrFilteredGrid` or other client-side filter controls.
 *
 */
export const AtDropdown = ({
  backgroundColor = 'bg-transparent',
  hasBorder,
  icon,
  label,
  options,
  value,
  onChange,
  isMulti = false,
}: IAtdropdownProps & { isMulti?: boolean }) => {
  const idBase = `dropdown-${label?.toLowerCase().replace(/\s+/g, '-')}`;
  const labelId = `${idBase}-label`;
  const [showFocusRing, setShowFocusRing] = useState(false);
  const hadKeyboardEvent = useRef(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') hadKeyboardEvent.current = true;
    };
    const handleMouseDown = () => {
      hadKeyboardEvent.current = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleMouseDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  const handleChange = (event: SelectChangeEvent<any>) => {
    // For multi-select, handle "clear all" option
    if (isMulti && event.target.value?.includes('__ALL__')) {
      onChange([]);
      return;
    }
    onChange(event.target.value);
  };

  // When passing value to Select, filter out empty strings for multi-select
  const normalizedValue = isMulti
    ? Array.isArray(value)
      ? value.filter((v) => v !== '')
      : []
    : (value ?? '');

  // Determine if the dropdown is "empty"
  const isEmpty =
    (isMulti &&
      (!normalizedValue ||
        (Array.isArray(normalizedValue) &&
          normalizedValue.filter((v) => v !== '').length === 0))) ||
    (!isMulti && (!value || value === ''));

  return (
    <Box
      className={classNames(
        'focus-ring-tabbed flex items-center overflow-hidden',
        {
          'ring-2 ring-dropdown ring-offset-2': showFocusRing,
          'border-2 border-skin-stroke-secondary': hasBorder && !showFocusRing,
        },
        backgroundColor,
      )}
      sx={{
        borderRadius: '15px',
        height: '60px',
        backgroundColor: 'var(--color-white)',
        outline: 'none',
      }}
    >
      {icon && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            pl: 2,
            pr: 1,
          }}
        >
          <span className="text-2xl" data-testid="at-icon">
            <AtIcon icon={icon} color="var(--color-ui-icon-tertiary)" />
          </span>
        </Box>
      )}

      <FormControl fullWidth size="medium" sx={{ height: '100%' }}>
        {label && (
          <InputLabel id={labelId} htmlFor={idBase} className="sr-only">
            {label}
          </InputLabel>
        )}

        <Select
          labelId={labelId}
          id={idBase}
          displayEmpty
          sx={{
            borderRadius: '15px',
            backgroundColor: 'var(--color-white)',
            '& .MuiSelect-select': {
              backgroundColor: 'var(--color-white)',
            },
            // Remove default MUI outline on focus, rely on showFocusRing
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
          }}
          multiple={isMulti}
          value={normalizedValue}
          onChange={handleChange}
          input={
            <OutlinedInput
              onFocus={() => {
                if (hadKeyboardEvent.current) setShowFocusRing(true);
              }}
              onBlur={() => setShowFocusRing(false)}
              notched={false}
              sx={{
                height: '100%',
                backgroundColor: 'var(--color-white)',
                borderRadius: '50px',
                paddingLeft: 2,
                paddingRight: 2,
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  paddingY: 0,
                  backgroundColor: isEmpty
                    ? 'var(--color-neutral-100) !important'
                    : 'var(--color-white) !important',
                },
                '& .MuiSelect-select.MuiSelect-multiple': {
                  backgroundColor: isEmpty
                    ? 'var(--color-neutral-100) !important'
                    : 'var(--color-white) !important',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
              }}
            />
          }
          renderValue={(selected) => {
            const cleaned = Array.isArray(selected)
              ? selected.filter(Boolean)
              : selected;

            if (!cleaned || (Array.isArray(cleaned) && cleaned.length === 0)) {
              return <span className="text-skin-tertiary">{label}</span>;
            }

            if (!isMulti) {
              const selectedOption = options.find(
                (opt) => opt.value === cleaned,
              );
              return selectedOption?.label ?? cleaned;
            }

            if (Array.isArray(cleaned)) {
              return (
                <span
                  style={{
                    display: 'inline-block',
                    maxWidth: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    verticalAlign: 'bottom',
                  }}
                  title={cleaned
                    .map(
                      (val) =>
                        options.find((opt) => opt.value === val)?.label ?? val,
                    )
                    .join(', ')}
                >
                  {cleaned
                    .map(
                      (val) =>
                        options.find((opt) => opt.value === val)?.label ?? val,
                    )
                    .join(', ')}
                </span>
              );
            }

            return cleaned;
          }}
        >
          {!isMulti ? (
            <MenuItem
              value=""
              sx={{
                '&.Mui-selected': {
                  backgroundColor: '#babebf',
                  '&:hover': {
                    backgroundColor: '#babebf',
                  },
                },
              }}
              selected={isEmpty}
            >
              {isEmpty ? 'All' : '-- Clear Selection --'}
            </MenuItem>
          ) : (
            <MenuItem
              value="__ALL__"
              sx={{
                '&.Mui-selected': {
                  backgroundColor: '#babebf',
                  '&:hover': {
                    backgroundColor: '#babebf',
                  },
                },
              }}
              selected={isEmpty}
            >
              <Checkbox
                checked={isEmpty}
                sx={{
                  color: '#3d382e',
                  '&.Mui-checked': {
                    color: '#3d382e',
                  },
                }}
              />
              <ListItemText primary="All" />
            </MenuItem>
          )}
          {/* Actual options */}
          {options.map(
            ({ label: optionLabel, value: optionValue }: IOption) => (
              <MenuItem
                key={optionValue}
                value={optionValue}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: '#babebf',
                    '&:hover': {
                      backgroundColor: '#babebf',
                    },
                  },
                }}
              >
                {isMulti && (
                  <Checkbox
                    checked={
                      Array.isArray(normalizedValue) &&
                      normalizedValue.indexOf(optionValue) > -1
                    }
                    sx={{
                      color: '#3d382e',
                      '&.Mui-checked': {
                        color: '#3d382e',
                      },
                    }}
                  />
                )}
                <ListItemText primary={optionLabel} />
              </MenuItem>
            ),
          )}
        </Select>
      </FormControl>
    </Box>
  );
};
